import { ArrowDownToLine, ArrowUpToLine, Loader2 } from "lucide-react";
import type { EmailLog } from "@/types/email";

type EmailLogListProps = {
  logs: EmailLog[];
  loading?: boolean;
  onSelect?: (log: EmailLog) => void;
};

const STATUS_COLORS: Record<string, string> = {
  sent: "text-success",
  queued: "text-primary",
  failed: "text-destructive",
  opened: "text-success",
  replied: "text-info",
  received: "text-info",
};

export function EmailLogList({ logs, loading, onSelect }: EmailLogListProps) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 aa-glass-card px-4 py-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin text-primary" />
        Loading activity…
      </div>
    );
  }

  if (!logs.length) {
    return (
      <div className="aa-glass-card border-dashed border-border/70 px-4 py-5 text-center text-sm text-muted-foreground">
        No email activity logged yet.
      </div>
    );
  }

  return (
    <ul className="space-y-3">
      {logs.map((log) => {
        const statusColor = STATUS_COLORS[log.status?.toLowerCase()] ?? "text-foreground/80";
        const direction = log.direction === "inbound" ? "Inbound" : "Outbound";
        const DirectionIcon = log.direction === "inbound" ? ArrowDownToLine : ArrowUpToLine;
        const target =
          log.direction === "inbound" ? log.from_address ?? log.to_address : log.to_address ?? log.from_address;
        return (
          <li
            key={log.id}
            className={`aa-glass-card px-4 py-3 transition-all duration-200 ${
              onSelect ? "cursor-pointer hover:border-primary/60" : ""
            }`}
            onClick={() => onSelect?.(log)}
          >
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className={`font-semibold uppercase tracking-wide ${statusColor}`}>{log.status}</span>
              {log.sent_at && <span className="text-muted-foreground">{formatRelative(log.sent_at)}</span>}
            </div>
            <div className="mt-1 flex items-center gap-2 text-sm text-foreground/80">
              <DirectionIcon className="h-4 w-4 text-muted-foreground" />
              <p className="font-semibold">{log.subject || "(No subject)"}</p>
            </div>
            <p className="text-xs text-muted-foreground">{target || "Unknown participant"}</p>
            {log.error_message && <p className="mt-1 text-xs text-destructive">{log.error_message}</p>}
            {log.direction === "inbound" && !log.contact_id && log.suggested_contact_id && (
              <p className="mt-2 text-[11px] uppercase tracking-wide text-primary">
                Suggested contact #{log.suggested_contact_id}
              </p>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function formatRelative(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const diffMs = date.getTime() - Date.now();
  const diffMinutes = Math.round(diffMs / 60000);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (Math.abs(diffMinutes) < 60) {
    return formatter.format(diffMinutes, "minute");
  }
  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 24) {
    return formatter.format(diffHours, "hour");
  }
  const diffDays = Math.round(diffHours / 24);
  return formatter.format(diffDays, "day");
}
